import { Component } from '@theme/component';
import { formatMoney } from '@theme/money-formatting';
import { CartAddEvent } from '@theme/events';

/**
 * ════════════════════════════════════════════════════════════════════════
 * Bundle Builder v2 — state machine + orchestrator (Phase 0 foundation)
 * ────────────────────────────────────────────────────────────────────────
 * `<bundle-builder>` is the single source of truth. It reads the normalized
 * config emitted by `sections/bundle-builder.liquid`, holds selection state
 * for every step, computes pricing, and broadcasts a `bundle:update` event.
 *
 * Child components (`<bundle-step>`, `<bundle-bottom-bar>`, `<bundle-review>`,
 * `<bundle-personalization>`, `<bundle-addon>`, `<bundle-accident>`,
 * `<bundle-details-drawer>`, `<bundle-gallery>`, `<bundle-reviews>`) are
 * presentation only — they listen for `bundle:update` and render. They mutate
 * state by calling methods on the host `<bundle-builder>` via the declarative
 * `on:` bindings.
 *
 * Each child lives in its OWN snippet + asset pair (bb-*.liquid + bb-*.js) and
 * self-registers. `<bundle-step>` is in assets/bb-step.js and
 * `<bundle-bottom-bar>` is in assets/bb-bottom-bar.js. This orchestrator file
 * owns ONLY `<bundle-builder>`.
 *
 * PUBLIC CONTRACT for Phase 1 component authors:
 *
 *   Read state:
 *     builder.config                      → normalized config (frozen)
 *     builder.state                       → { currentStep, steps: StepState[] }
 *     builder.totals                      → { subtotal, discount, total, savings } (cents)
 *     builder.getStepSelection(i)         → resolved selection for step i:
 *                                           { step, state, group, product, variant }
 *     builder.getPersonalization(i[, p])  → embroidery offer for the selected
 *                                           product (step-level fallback), or null
 *     builder.selectionDelta(i)           → signed cents: what step i's current
 *                                           pick adds to the bundle as merchandised
 *     builder.sizeOptions(i)              → [{groupIndex, productId, label, active}]
 *                                           divider-count siblings of the current pick
 *     builder.money(cents)                → shop-formatted price string
 *
 *   `variant` is the RESOLVED purchasable variant — read price and cart ids off
 *   it, never off `product.price` / `product.variantId` (those are only the
 *   product's default; a product may have several differently-priced variants).
 *
 *   Mutate (call from on: bindings; handler signature is (data, event)):
 *     on:click="bundle-builder/selectGroup?step=0&group=1"
 *     on:change="bundle-builder/selectProduct"        // reads data-* on the input
 *     on:click="bundle-builder/selectVariant?step=0"  // reads data-variant-id on the button
 *     on:click="bundle-builder/selectSize?step=0"     // reads data-group + data-product-id
 *     on:change="bundle-builder/toggleAddOn?step=0"
 *     on:change="bundle-builder/togglePersonalization?step=0"
 *     on:input="bundle-builder/setPersonalizationField?step=0"   // data-field on input
 *     on:change="bundle-builder/selectAccident?step=0"           // value = variantId
 *     on:click="bundle-builder/goToStep?step=2"
 *     on:click="bundle-builder/next"
 *     on:click="bundle-builder/prev"
 *     on:click="bundle-builder/complete"
 *     on:click="bundle-builder/openDetails?step=0"   // opens the See Details drawer
 *
 *   Events (bubbling, dispatched on the builder element):
 *     bundle:update   detail = { currentStep, steps, totals }
 *     bundle:step     detail = { from, to }
 *     bundle:complete detail = { items, totals }   // line items ready for /cart/add
 *     bundle:details  detail = { step, product }   // See Details drawer listens
 * ════════════════════════════════════════════════════════════════════════
 */

/**
 * @typedef {Object} StepState
 * @property {number} groupIndex
 * @property {string|null} productId
 * @property {string|null} variantId
 * @property {boolean} addOnSelected
 * @property {boolean} personalizationOn
 * @property {Record<string,string>} personalizationProps
 * @property {string|null} accidentVariantId
 */

export class BundleBuilderComponent extends Component {
  /** @type {any} */ config = {};
  /** @type {{ currentStep:number, steps:StepState[] }} */
  state = { currentStep: 0, steps: [] };

  connectedCallback() {
    super.connectedCallback();
    this.config = this.#readConfig();
    this.state = {
      currentStep: 0,
      // `steps` is the working DRAFT per step (what the step UI reflects as you
      // pick). `saved` is the COMMITTED selection used by the bottom bar, totals,
      // review and cart — it only changes when Save & Continue is pressed, so
      // nothing "commits" until save. Starts all-null (Figma open/empty state).
      steps: this.config.steps.map((step, i, arr) => this.#defaultStepState(step, i, arr.length)),
      saved: this.config.steps.map(() => null),
    };
    // Tier deltas are NOT derived here. They are relative to whatever the step
    // is currently showing, so they change as the shopper picks — see
    // `groupDelta`, which bb-step.js calls on every update.
    Object.freeze(this.config);
    // Honour anything the shopper already tapped while this host was still
    // downloading — the DOM is the only place that tap was recorded.
    this.#adoptPreHydrationSelections();
    // Child components live in separate module scripts and upgrade in an
    // undefined order relative to this host. Emit now AND on the next frame so
    // components that attach their bundle:update listener after this host
    // connects still receive the initial render.
    this.#emitUpdate();
    requestAnimationFrame(() => this.#emitUpdate());
  }

  /**
   * Re-broadcast current state on demand (e.g. a component that connects late).
   * Coalesced: every child calls this from its connectedCallback, so a dozen
   * components used to trigger a dozen full broadcasts during load. One
   * microtask collects them into a single emit, still before first paint.
   */
  requestUpdate() {
    if (this.#updateQueued) return;
    this.#updateQueued = true;
    queueMicrotask(() => {
      this.#updateQueued = false;
      if (this.isConnected) this.#emitUpdate();
    });
  }

  #updateQueued = false;

  /**
   * Emit on the next frame, collapsing bursts (typing into a personalization
   * field fires per keystroke; each emit re-runs every step's render).
   */
  #emitUpdateSoon() {
    if (this.#emitFrame) return;
    this.#emitFrame = requestAnimationFrame(() => {
      this.#emitFrame = 0;
      this.#emitUpdate();
    });
  }

  #emitFrame = 0;

  /**
   * Label for the "finish" CTAs (last step button, bottom bar). Follows the
   * section's "On bundle complete" setting so a bar that says "Checkout" never
   * silently adds to cart instead.
   */
  get ctaLabel() {
    return (this.config.atcMode || 'checkout') === 'checkout' ? 'Checkout' : 'Add to Cart';
  }

  /** @returns {any} */
  #readConfig() {
    const fallback = { steps: [], discount: 0, atcMode: 'checkout', moneyFormat: '${{amount}}' };
    const el = this.querySelector('script[type="application/json"][ref="config"]');
    if (!el?.textContent) return fallback;
    try {
      // Order matters: resolve the step-level add-on down onto the groups
      // FIRST, so the duplicate check below sees the value every consumer
      // will actually read.
      return this.#markProductPersonalization(
        this.#dropDuplicateAddOns(this.#applyStepAddOns(this.#inflateFragments(JSON.parse(el.textContent))))
      );
    } catch (error) {
      console.error('[bundle-builder] invalid config JSON', error);
      return fallback;
    }
  }

  /**
   * Expand shared rich-text fragments back onto each product.
   *
   * The section emits the `whyBuy` / `features` / `specs` metafield HTML once
   * per distinct value in `config.fragments` (keyed by content hash) and puts a
   * `frag:<hash>` reference on each product — on the Loma bundle 56 products
   * shared 6 distinct "why buy" blocks, so inlining them was ~50 KB of repeated
   * markup. Anything that isn't a reference passes through untouched.
   * @param {any} config
   */
  #inflateFragments(config) {
    const fragments = config?.fragments;
    if (!fragments || typeof fragments !== 'object') return config;
    const resolve = (value) =>
      typeof value === 'string' && value.startsWith('frag:') ? fragments[value.slice(5)] ?? '' : value;
    for (const step of config.steps ?? []) {
      for (const group of step.groups ?? []) {
        for (const product of group.products ?? []) {
          for (const key of ['whyBuy', 'features', 'specs', 'detail']) product[key] = resolve(product[key]);
        }
      }
    }
    delete config.fragments;
    return config;
  }

  /**
   * Push the STEP's add-on down onto every group in that step.
   *
   * An add-on belongs to the step, not to one material tier: configuring it on
   * each `product_group` meant it disappeared when the shopper switched tabs,
   * and allowed the same product to be reached twice. `bundle_selection_step`
   * is the source of truth.
   *
   * The group's own `add_on` remains as a FALLBACK so bundles still carrying
   * group-level data keep working until it is migrated — today that is Big
   * Frosty on the shared `accessory` step. Once the data moves, the fallback
   * (and `product_group.add_on`) can go.
   *
   * Resolving here rather than at each call site means row visibility,
   * subtotal, cart build and the details drawer all inherit it, since they
   * read `group.addOn`.
   *
   * @param {any} config
   * @returns {any}
   */
  #applyStepAddOns(config) {
    for (const step of config?.steps ?? []) {
      if (!step?.addOn) continue;
      for (const group of step.groups ?? []) group.addOn = step.addOn;
    }
    return config;
  }

  /**
   * Drop a group add-on that IS the step's accident-protection product.
   *
   * A bundle can be wired with the same product in both
   * `product_group.add_on` and `bundle_selection_step.accident_protection`.
   * The Add Ons panel then offers it twice — once as the group add-on row
   * (`<bundle-addon>`) and once as the protection row (`<bundle-accident>`) —
   * and checking both would put it in the cart twice. Accident protection owns
   * that product (it carries the coverage-tier variants), so the group add-on
   * loses. Normalising here keeps visibility, pricing, cart and the details
   * drawer consistent, since they all read `group.addOn`.
   *
   * @param {any} config
   * @returns {any}
   */
  #dropDuplicateAddOns(config) {
    for (const step of config?.steps ?? []) {
      const protectedId = step?.accidentProtection?.productId;
      if (protectedId == null) continue;
      for (const group of step.groups ?? []) {
        // IDs come from Liquid as numbers but may be strings elsewhere.
        if (group.addOn && String(group.addOn.productId) === String(protectedId)) {
          group.addOn = null;
        }
      }
    }
    return config;
  }

  // ── Variant resolution ─────────────────────────────────────────────────────
  // A bundle product can carry several purchasable variants (the Recess kids
  // bags have a "Size" option whose two values differ in BOTH price and
  // availability). `product.variantId` / `product.price` are only the product's
  // DEFAULT variant, so everything that buys or prices must resolve the variant
  // the shopper actually chose.

  /**
   * Every variant of a product. Configs emitted before `variants` existed get a
   * synthesized single entry from the product-level default, so callers can read
   * `.id` / `.price` / `.available` off one shape either way.
   * @param {any} product
   * @returns {Array<any>}
   */
  #variantsOf(product) {
    if (!product) return [];
    if (Array.isArray(product.variants) && product.variants.length) return product.variants;
    if (product.variantId == null) return [];
    return [
      {
        id: product.variantId,
        title: product.title,
        available: product.available !== false,
        price: product.price ?? 0,
        compareAt: product.compareAt ?? product.price ?? 0,
        options: [],
      },
    ];
  }

  /**
   * The variant a selection refers to: the chosen id, else the first AVAILABLE
   * variant, else the first. The id is only honoured when it is found ON this
   * product, so a stale id from a previously selected product can never resolve.
   * @param {any} product
   * @param {string|number|null} [variantId]
   */
  #resolveVariant(product, variantId) {
    const variants = this.#variantsOf(product);
    if (!variants.length) return null;
    if (variantId != null) {
      // IDs come from Liquid as numbers but off data-* attributes as strings.
      const chosen = variants.find((v) => String(v.id) === String(variantId));
      if (chosen) return chosen;
    }
    return variants.find((v) => v.available !== false) ?? variants[0];
  }

  /** The first-available variant id for whatever product `st` currently points at. */
  #defaultVariantId(stepIndex, st) {
    const group = this.config.steps[stepIndex]?.groups?.[st.groupIndex];
    const product = group?.products?.find((p) => String(p.productId) === String(st.productId));
    return this.#resolveVariant(product, null)?.id ?? null;
  }

  /** Unit price of a resolved selection — always the CHOSEN variant's. */
  #unitPrice(sel) {
    return sel?.variant?.price ?? sel?.product?.price ?? 0;
  }

  /**
   * Flag steps whose products carry their own embroidery config.
   *
   * The step-level `personalization` metaobject is a MIGRATION fallback, for
   * bundles whose bags have not been given a `custom.embroidery_config` yet. Once
   * any bag in a step has one, the data is authoritative for that step: a bag
   * without a config genuinely cannot be embroidered (the Recess Kids bags), and
   * falling back to the step would offer embroidery that can't be produced.
   *
   * @param {any} config
   * @returns {any}
   */
  #markProductPersonalization(config) {
    for (const step of config?.steps ?? []) {
      step.usesProductPersonalization = (step.groups ?? []).some((group) =>
        (group.products ?? []).some((product) => product.personalization)
      );
    }
    return config;
  }

  /**
   * The first option the shopper could buy in a step, in merchandised order —
   * group order, then product order within it. Sold-out products are skipped,
   * falling back to the first group's entry when nothing in the step is
   * available so the step is never left empty.
   * @param {any} step
   */
  #firstAvailableOption(step) {
    const groups = step.groups || [];
    for (let gi = 0; gi < groups.length; gi++) {
      if ((groups[gi].products || []).some((p) => p.available !== false)) {
        const entry = this.#groupEntry(groups[gi]);
        if (entry) return { gi, ...entry };
      }
    }
    const entry = this.#groupEntry(groups[0]);
    return entry ? { gi: 0, ...entry } : null;
  }

  /** @param {any} step @param {number} [index] @param {number} [total] @returns {StepState} */
  #defaultStepState(step, index, total) {
    // The LAST step (accessory) starts UNSELECTED — the shopper must pick one,
    // and the CTA stays "Make a Selection" until they do. It is still PRICED,
    // though: see `#pricingSelection`.
    //
    // Every other step opens on the first option it can actually sell, in
    // merchandised order — the one its gallery is already showing — rather than
    // whichever option happens to be cheapest. Merchandised order is the
    // deliberate one; cheapest surfaces whatever is discounted deepest, which on
    // the Ryder bag step was a clearance colourway priced $50 under its ten
    // siblings, and on the accessory step a $25 glove ahead of the $34.99 cooler
    // the step leads with.
    const isLast = typeof index === 'number' && typeof total === 'number' && index === total - 1;
    const best = isLast ? null : this.#firstAvailableOption(step);
    return {
      groupIndex: best?.gi ?? 0,
      productId: best?.product?.productId ?? null,
      variantId: best?.variant?.id ?? best?.product?.variantId ?? null,
      addOnSelected: false,
      personalizationOn: false,
      personalizationProps: {},
      accidentVariantId: null,
    };
  }

  /**
   * Adopt any swatch the shopper checked BEFORE this component upgraded.
   *
   * The step markup is server-rendered, so it is on screen and partly live the
   * moment it parses — but a swatch only reaches `selectProduct` once this host
   * has upgraded, and on a cold cache over mobile data that is seconds later.
   * A tap inside that window still CHECKS the radio, because label-to-radio
   * activation is native and needs no JS: the selection ring moves and the
   * shopper reads the pick as registered, while `state` still holds the server
   * default. The step then names, prices, and ultimately CARTS the wrong
   * colourway, and nothing reconciles the two afterwards — no later update ever
   * reads the radios back.
   *
   * So at connect time the DOM wins, and the tap is replayed through
   * `selectProduct` — the same path the live binding uses — so variant
   * re-derivation and the last step's immediate commit behave exactly as they
   * would have had the tap landed a second later.
   *
   * The `defaultChecked` test is what separates a real tap from the server's own
   * pre-selection: the snippet pre-checks each step's first swatch with the
   * `checked` ATTRIBUTE, which `defaultChecked` reflects and which a tap never
   * touches. Without it this would also "adopt" that pre-checked swatch and
   * override the availability fallback `#defaultStepState` applies when a step's
   * first group opens sold out.
   */
  #adoptPreHydrationSelections() {
    for (const input of this.querySelectorAll('.bb-swatch input[type="radio"]:checked')) {
      if (!(input instanceof HTMLInputElement) || input.defaultChecked) continue;
      const state = this.state.steps[Number(input.dataset.step)];
      // Ignore a radio naming a step this builder doesn't have, and the case
      // where the tap happens to agree with what state already holds.
      if (!state || String(input.dataset.productId ?? '') === String(state.productId ?? '')) continue;
      this.selectProduct({ target: input });
    }
  }

  /** @param {number} stepIndex */
  #isLastStep(stepIndex) {
    return stepIndex === this.config.steps.length - 1;
  }

  // ── State accessors ──────────────────────────────────────────────────────

  /** @param {number} stepIndex */
  getStep(stepIndex) {
    return this.config.steps[stepIndex];
  }

  /** Resolve a step's config + a given selection state into {step,state,group,product,variant}. */
  #resolve(stepIndex, st) {
    const step = this.config.steps[stepIndex];
    if (!step || !st) return null;
    const group = step.groups[st.groupIndex];
    // IDs from config are numbers (`p.id | json`) but data-* attributes are
    // strings — compare as strings so a swatch click actually matches.
    let product = null;
    if (group) {
      if (st.productId != null) {
        product = group.products.find((p) => String(p.productId) === String(st.productId)) ?? null;
      }
      // Non-last steps fall back to the first product so the UI always has a
      // selection; the last step (accessory) shows NOTHING until chosen.
      if (!product && st.productId == null && !this.#isLastStep(stepIndex)) {
        product = group.products[0];
      }
    }
    // The purchasable variant this selection means. Null when no product is
    // resolved (the accessory step before a pick).
    const variant = this.#resolveVariant(product, st.variantId);
    return { step, state: st, group, product, variant };
  }

  /** The in-progress (draft) selection for a step — what the step UI shows. */
  getDraftSelection(stepIndex) {
    return this.#resolve(stepIndex, this.state.steps[stepIndex]);
  }

  /**
   * The COMMITTED selection for a step — what the bottom bar, totals, review
   * and cart use. Null product until the step is saved.
   */
  getStepSelection(stepIndex) {
    const saved = this.state.saved[stepIndex];
    if (!saved) return { step: this.config.steps[stepIndex], state: null, group: null, product: null, variant: null };
    return this.#resolve(stepIndex, saved);
  }

  /** Commit the working draft of a step into its saved selection. */
  #commit(stepIndex) {
    const w = this.state.steps[stepIndex];
    if (!w) return;
    this.state.saved[stepIndex] = { ...w, personalizationProps: { ...w.personalizationProps } };
  }

  /**
   * Resolve the personalization offer for a step.
   *
   * Personalization belongs to the PRODUCT: every bag carries its own
   * `custom.embroidery_config` (the metaobject the standard PDP reads), so what
   * the row offers — fee, character limit, line count, preview, thread — changes
   * with the selected bag, and a bag without one offers nothing at all.
   *
   * Order: selected product → step-level metaobject → none. The step level is
   * kept purely as a fallback for bundles whose bags have not been given a
   * config yet, and drops out as soon as any bag in the step has one
   * (`usesProductPersonalization`) — see #markProductPersonalization. Once every
   * bag has one, `personalization` on `bundle_selection_step` can go.
   *
   * @param {number} stepIndex
   * @param {any} [product] - the product to resolve against; omit for the draft pick.
   * @returns {any|null}
   */
  getPersonalization(stepIndex, product) {
    const step = this.config.steps[stepIndex];
    if (!step) return null;
    const resolved = product === undefined ? this.getDraftSelection(stepIndex)?.product : product;
    if (resolved?.personalization) return resolved.personalization;
    if (resolved && step.usesProductPersonalization) return null;
    return step.personalization ?? null;
  }

  /**
   * Drop a personalization toggle left on for a bag that can't be embroidered.
   *
   * The shopper can switch from a bag WITH an embroidery config to one without
   * (a Recess Kids bag) while the row is checked. Without this, `totals` would
   * keep charging a fee and `buildCartItems` would stamp embroidery properties
   * — the Fulfil channel keys — onto a bag that cannot be embroidered.
   */
  #dropUnavailablePersonalization() {
    for (let i = 0; i < this.config.steps.length; i++) {
      const draft = this.state.steps[i];
      if (draft?.personalizationOn && !this.getPersonalization(i, this.getDraftSelection(i)?.product ?? null)) {
        draft.personalizationOn = false;
      }
      const saved = this.state.saved[i];
      if (saved?.personalizationOn && !this.getPersonalization(i, this.getStepSelection(i)?.product ?? null)) {
        saved.personalizationOn = false;
      }
    }
  }

  /**
   * The product a step lands on when the shopper switches to `group` — the same
   * pick `selectGroup` commits, so a tab's "+$x" always states what clicking it
   * actually costs rather than a figure derived some other way. First AVAILABLE
   * in merchandised order, falling back to the first product when the whole
   * group is sold out so a group is never priced as nothing.
   * @returns {{product: any, variant: any}|null}
   */
  #groupEntry(group) {
    const products = group?.products || [];
    const product = products.find((p) => p.available !== false) ?? products[0] ?? null;
    if (!product) return null;
    return { product, variant: this.#resolveVariant(product, null) };
  }

  /**
   * What switching this step to `groupIndex` would add to (or take off) the
   * bundle, against whatever the step is showing now. Signed — negative when
   * the group is cheaper than the current pick — and recomputed on every
   * update, so the tabs re-baseline as the shopper changes their mind rather
   * than staying relative to a fixed cheapest-in-step.
   * @param {number} stepIndex
   * @param {number} groupIndex
   * @returns {number} cents
   */
  groupDelta(stepIndex, groupIndex) {
    const entry = this.#groupEntry(this.config.steps[stepIndex]?.groups[groupIndex]);
    if (!entry) return 0;
    const entryPrice = entry.variant?.price ?? entry.product.price ?? 0;
    return entryPrice - this.#unitPrice(this.#pricingSelection(stepIndex));
  }

  /**
   * What the step's CURRENT pick adds to (or takes off) the bundle as
   * merchandised — the chosen variant's price against the option the step opens
   * on (`#firstAvailableOption`), which is also what `totals` prices an
   * untouched step at. Signed, and 0 while the shopper is still on the default.
   *
   * A different baseline to `groupDelta` because it answers a different
   * question. The tabs asked "what would switching to that tier cost?", which
   * is meaningless for the tier you are already standing on; this figure now
   * sits on the selected colour line, where the only useful comparison is
   * against the bundle price the shopper was quoted.
   * @param {number} stepIndex
   * @returns {number} cents
   */
  selectionDelta(stepIndex) {
    const draft = this.getDraftSelection(stepIndex);
    if (!draft?.product) return 0;
    const step = this.config.steps[stepIndex];
    const base = step ? this.#firstAvailableOption(step) : null;
    if (!base) return 0;
    return this.#unitPrice(draft) - (base.variant?.price ?? base.product?.price ?? 0);
  }

  /**
   * Pricing follows the LIVE DRAFT (current product + add-ons) for every step,
   * so the total updates as selections and add-ons change. The draft defaults to
   * each step's first available option, so on load the bar shows the bundle as
   * merchandised; it then tracks edits in real time (the slot cards still commit
   * on Save).
   *
   * Steps with no product yet price at their first available option, the same
   * one every other step opens on. The last step deliberately starts unselected
   * (see `#defaultStepState`), so without this the bar would advertise a bundle
   * missing one component. Test the PRODUCT, not the draft: `getDraftSelection`
   * always returns a resolved shape, so a `??` on the draft never falls through.
   */
  #pricingSelection(stepIndex) {
    const draft = this.getDraftSelection(stepIndex);
    if (draft?.product) return draft;
    const step = this.config.steps[stepIndex];
    const entry = step ? this.#firstAvailableOption(step) : null;
    return entry ? { step, group: step.groups[entry.gi], product: entry.product, variant: entry.variant, state: null } : null;
  }

  get totals() {
    let subtotal = 0;
    for (let i = 0; i < this.config.steps.length; i++) {
      const sel = this.#pricingSelection(i);
      if (!sel?.product) continue;
      // The CHOSEN variant's price — a size change must move the running total.
      subtotal += this.#unitPrice(sel);
      // Add-ons only count once a step is saved (sel.state present).
      if (sel.state?.addOnSelected && sel.group.addOn) subtotal += sel.group.addOn.price;
      if (sel.state?.personalizationOn) {
        const pers = this.getPersonalization(i, sel.product);
        if (pers) subtotal += pers.feePrice || 0;
      }
      if (sel.state?.accidentVariantId) {
        const v = sel.step.accidentProtection?.variants.find((x) => String(x.id) === String(sel.state.accidentVariantId));
        if (v) subtotal += v.price;
      }
    }
    // `bundle_discount` is a FIXED amount in the shop's major unit (e.g. 46.96 = $46.96),
    // matching the existing data + old system. The "SAVE X%" badge is derived from it.
    const discount = Math.min(subtotal, Math.round((this.config.discount || 0) * 100));
    const percent = subtotal > 0 ? Math.round((discount / subtotal) * 100) : 0;
    return { subtotal, discount, total: subtotal - discount, savings: discount, percent };
  }

  // ── Mutations (called from declarative on: bindings) ───────────────────────

  /** @param {{step:number, group:number}} data */
  selectGroup(data) {
    const st = this.state.steps[data.step];
    const group = this.config.steps[data.step]?.groups[data.group];
    if (!st || !group) return;
    st.groupIndex = data.group;
    st.addOnSelected = false; // add-on is group-scoped
    if (this.#isLastStep(data.step)) {
      // Switching accessory TYPE doesn't pick a colour — the shopper must click
      // a swatch. Clear any prior selection (and its commit) so the CTA reverts
      // to "Make a Selection" and the bar slot empties.
      st.productId = null;
      st.variantId = null;
      this.state.saved[data.step] = null;
    } else {
      // Same helper the tab's "+$x" is priced from, so the figure the shopper
      // was shown is the one they land on. The variant is re-derived from the
      // NEW product (its first available one) — an id belonging to the
      // previously selected product must never leak.
      const entry = this.#groupEntry(group);
      st.productId = entry?.product?.productId ?? null;
      st.variantId = entry?.variant?.id ?? null;
    }
    this.#emitUpdate();
  }

  /**
   * Color swatch change. Bound with no `?data`, so the framework calls this as
   * (event); but the event is always the LAST arg, so read it that way to stay
   * correct whether or not data is ever added to the binding.
   * Reads data-step / data-product-id / data-variant-id off the changed input.
   */
  selectProduct(...args) {
    const event = /** @type {Event} */ (args[args.length - 1]);
    let el = /** @type {HTMLElement} */ (event?.target);
    // A <select> (accessory dropdown) carries the data on its chosen <option>.
    if (el && el.tagName === 'SELECT') el = /** @type {HTMLSelectElement} */ (el).selectedOptions[0] || el;
    const step = Number(el?.dataset.step ?? event?.target?.dataset?.step);
    const st = this.state.steps[step];
    if (!st || !el) return;
    // The accessory step's single dropdown spans every group, so an option can
    // belong to a different group than the one currently active. Move the index
    // with it — #resolve looks the product up inside state.groupIndex.
    if (el.dataset.group != null) st.groupIndex = Number(el.dataset.group);
    st.productId = el.dataset.productId ?? null;
    // Re-derive from the newly selected product (its first available variant)
    // rather than trusting the swatch's data-variant-id, so a variant id from
    // the previous product can never leak into this step's state.
    st.variantId = this.#defaultVariantId(step, st) ?? el.dataset.variantId ?? null;
    // The last step has no Save button — picking a swatch IS the selection, so
    // commit it immediately: the bottom-bar card fills and the CTA enables.
    if (this.#isLastStep(step)) this.#commit(step);
    this.#emitUpdate();
  }

  /**
   * Variant button (e.g. the "Size" row). Bound as
   * `on:click="bundle-builder/selectVariant?step=N"`; the chosen id rides on the
   * button's `data-variant-id`, so the row can be rebuilt by bb-step.js without
   * the binding needing to know which variants exist.
   * @param {{step:number}|number} data
   */
  selectVariant(data, event) {
    const step = typeof data === 'object' ? Number(data.step) : Number(data);
    const st = this.state.steps[step];
    const target = /** @type {HTMLElement|null} */ (event?.target ?? null);
    const el = /** @type {HTMLElement|null} */ (target?.closest?.('[data-variant-id]') ?? target);
    const variantId = el?.dataset?.variantId;
    if (!st || !variantId) return;
    st.variantId = variantId;
    // Same last-step semantics as a swatch: there is no Save button, so choosing
    // a variant there IS the selection and commits immediately.
    if (this.#isLastStep(step) && this.getDraftSelection(step)?.product) this.#commit(step);
    this.#emitUpdate();
  }

  // ── Size ("way") siblings ──────────────────────────────────────────────────
  // The divider count of a bag (5-Way / 14-Way / Lefty) is not an option on the
  // product: each one is its OWN product, normally sitting in a different tier
  // group of the same step (Ryder Classic → Ryder 14). The section emits, per
  // product, which size universes claim it (`sizeFamilies`) and which colourway
  // it is (`sizeColor`); pairing happens here, and only ever against products
  // this step actually sells.

  /**
   * Colourway equality, matching snippets/linked-product-swatches.liquid so the
   * PDP and the bundle pair the same products. The containment fallback is the
   * PDP's tolerance for lines that spell the bag type differently after the
   * colour ("Toasted Almond" vs "Toasted Almond Carry Bag").
   * @param {string|null} a @param {string|null} b
   */
  #sameColorway(a, b) {
    if (!a || !b) return false;
    return a === b || a.includes(b) || b.includes(a);
  }

  /**
   * The divider counts reachable from a step's current pick, in merchandised
   * order (group order, then product order within it).
   *
   * Returns [] unless there are at least two — a lone "5-Way" is not a choice,
   * and only 3 of the Ryder bundle's 33 colourways have a counterpart at all,
   * so the row this feeds is hidden far more often than it is shown. Sold-out
   * counterparts are dropped rather than shown disabled: switching size is a
   * PRODUCT switch, so an unbuyable pill would strand the whole step. The
   * current pick is always included, sold out or not, so it can render pressed.
   *
   * @param {number} stepIndex
   * @returns {Array<{groupIndex:number, productId:any, label:string, name:string, active:boolean}>}
   */
  /**
   * Is this label a divider count ("5-Way", "14-Way") rather than some other
   * axis? `size-way-label` is shared with the PDP, whose pill row is a general
   * "other ways to buy this bag" strip and also carries Lefty, S-Class and
   * Storm. Unmapped families fall through to their own name, so a genuine
   * divider count is exactly what that map rewrote to "N-Way".
   * @param {string | null | undefined} label
   */
  static isDividerCount(label) {
    return /^\d+-Way$/i.test(String(label ?? ''));
  }

  sizeOptions(stepIndex) {
    const step = this.config.steps[stepIndex];
    const current = this.getDraftSelection(stepIndex)?.product;
    if (!step || !current?.sizeLabel) return [];
    // The row only makes sense for a bag that HAS a divider count. A Lefty is
    // defined by its grip, and offering it 5-Way/14-Way would read as a size
    // choice while actually switching the shopper out of left-handed entirely.
    // Same for the S-Class and Storm lines. They are reachable from the tier
    // tabs, which is where changing product line belongs.
    if (!BundleBuilderComponent.isDividerCount(current.sizeLabel)) return [];
    const families = new Set(current.sizeFamilies ?? []);
    if (!families.size) return [];

    // Keyed by label so a family that lists the same way twice renders one pill;
    // the current product wins the key outright, so `active` can never be lost
    // to a duplicate that happened to be merchandised earlier.
    /** @type {Map<string, {groupIndex:number, productId:any, label:string, name:string, active:boolean}>} */
    const byLabel = new Map();
    const groups = step.groups ?? [];
    for (let groupIndex = 0; groupIndex < groups.length; groupIndex++) {
      for (const product of groups[groupIndex].products ?? []) {
        if (!BundleBuilderComponent.isDividerCount(product.sizeLabel)) continue;
        const active = String(product.productId) === String(current.productId);
        if (!active && product.available === false) continue;
        if (!(product.sizeFamilies ?? []).some((family) => families.has(family))) continue;
        if (!this.#sameColorway(product.sizeColor, current.sizeColor)) continue;
        if (byLabel.has(product.sizeLabel) && !active) continue;
        byLabel.set(product.sizeLabel, {
          groupIndex,
          productId: product.productId,
          label: product.sizeLabel,
          name: product.sizeName,
          active,
        });
      }
    }
    return byLabel.size > 1 ? [...byLabel.values()] : [];
  }

  /**
   * Size pill. The counterpart is a DIFFERENT PRODUCT in a possibly different
   * tier group, so `groupIndex`, `productId` and `variantId` all move together —
   * `#resolve` looks the product up *inside* `state.groupIndex`, so a
   * half-applied switch resolves to nothing and empties the step. The pill's
   * product is verified to exist in that group first: a selection must never
   * point at something this step can't sell.
   * @param {{step:number}|number} data
   */
  selectSize(data, event) {
    const stepIndex = typeof data === 'object' ? Number(data.step) : Number(data);
    const st = this.state.steps[stepIndex];
    const target = /** @type {HTMLElement|null} */ (event?.target ?? null);
    const el = /** @type {HTMLElement|null} */ (target?.closest?.('[data-product-id]') ?? null);
    if (!st || !el) return;
    const groupIndex = Number(el.dataset.group);
    const group = this.config.steps[stepIndex]?.groups?.[groupIndex];
    const product = group?.products?.find((p) => String(p.productId) === String(el.dataset.productId));
    if (!product) return;

    // The add-on is group-scoped, which is why `selectGroup` clears it. Here the
    // group change is incidental to a size change, so only drop it when the
    // group swap actually changes WHICH add-on is on offer — after
    // #applyStepAddOns the tiers of a step usually share one, and silently
    // unchecking the shopper's cooler because they wanted 14 dividers is a
    // surprise, not a safeguard.
    const previousAddOn = this.config.steps[stepIndex]?.groups?.[st.groupIndex]?.addOn?.productId ?? null;
    if (String(group.addOn?.productId ?? '') !== String(previousAddOn ?? '')) st.addOnSelected = false;

    st.groupIndex = groupIndex;
    st.productId = product.productId;
    // Re-derived from the NEW product, exactly as selectProduct does: a variant
    // id belonging to the bag being switched away from must never leak across.
    st.variantId = this.#defaultVariantId(stepIndex, st);
    // Same last-step semantics as a swatch — no Save button there, so the pick
    // IS the selection.
    if (this.#isLastStep(stepIndex)) this.#commit(stepIndex);
    this.#emitUpdate();
  }

  /** @param {{step:number}} data */
  toggleAddOn(data, event) {
    const st = this.state.steps[data.step];
    if (!st) return;
    st.addOnSelected = /** @type {HTMLInputElement} */ (event.target).checked;
    this.#emitUpdate();
  }

  /** @param {{step:number}} data */
  togglePersonalization(data, event) {
    const st = this.state.steps[data.step];
    if (!st) return;
    const target = /** @type {HTMLInputElement} */ (event.target);
    st.personalizationOn = target.type === 'checkbox' ? target.checked : target.value === 'yes';
    this.#emitUpdate();
  }

  /**
   * Embroidery text the machine can actually stitch: uppercase A-Z, digits and
   * spaces, matching the PDP popup (snippets/embroidery-popup.liquid) so both
   * routes to the same Fulfil fields agree on what is allowed.
   * @param {string} value
   */
  static sanitizeEmbroideryText(value) {
    return String(value ?? '')
      .toUpperCase()
      .replace(/[^A-Z0-9 ]/g, '');
  }

  /** @param {{step:number}} data reads data-field off the input */
  setPersonalizationField(data, event) {
    const st = this.state.steps[data.step];
    const el = /** @type {HTMLInputElement} */ (event.target);
    if (!st || !el.dataset.field) return;
    let value = el.value;
    // Only the free-text lines. The thread colour is a <select> whose values are
    // Fulfil codes ("Toasted Almond"), which uppercasing would corrupt.
    if (el.tagName === 'INPUT') {
      value = BundleBuilderComponent.sanitizeEmbroideryText(value);
      // Write back so the shopper sees what will actually be stitched. Guarded:
      // reassigning on every keystroke would otherwise fight IME composition.
      if (el.value !== value) el.value = value;
    }
    st.personalizationProps[el.dataset.field] = value;
    if (st.personalizationOn) this.#setError('');
    this.#emitUpdateSoon();
  }

  /**
   * Checkbox to add/remove accident protection. On → default to the cheapest
   * tier (or keep an already-chosen tier); off → clear.
   * @param {{step:number}} data
   */
  toggleAccident(data, event) {
    const st = this.state.steps[data.step];
    if (!st) return;
    const on = /** @type {HTMLInputElement} */ (event.target).checked;
    if (on) {
      const variants = this.config.steps[data.step]?.accidentProtection?.variants ?? [];
      const cheapest = [...variants].sort((a, b) => a.price - b.price)[0];
      st.accidentVariantId = st.accidentVariantId || cheapest?.id || null;
    } else {
      st.accidentVariantId = null;
    }
    this.#emitUpdate();
  }

  /** @param {{step:number}} data tier dropdown — value = variantId */
  selectAccident(data, event) {
    const st = this.state.steps[data.step];
    if (!st) return;
    const value = /** @type {HTMLInputElement} */ (event.target).value;
    st.accidentVariantId = value || null;
    this.#emitUpdate();
  }

  // ── Navigation ─────────────────────────────────────────────────────────────

  /** @param {{step:number}|number} data */
  goToStep(data) {
    this.#setStep(typeof data === 'object' ? data.step : Number(data));
  }
  /**
   * Personalization is on but the first text line is blank. Whitespace doesn't
   * count — it satisfies `maxlength` and reaches Fulfil as an empty stitch.
   * @param {number} stepIndex
   * @returns {boolean}
   */
  #personalizationIncomplete(stepIndex) {
    const st = this.state.steps[stepIndex];
    if (!st?.personalizationOn) return false;
    const pers = this.getPersonalization(stepIndex);
    if (!pers?.feeVariantId) return false; // no fee = no embroidery ordered, nothing to require
    const fields = Array.isArray(pers.fields) ? pers.fields : Object.keys(st.personalizationProps || {});
    const firstText = fields.find((f) => !/colou?r|thread/i.test(f));
    if (!firstText) return false;
    return String(st.personalizationProps?.[firstText] ?? '').trim() === '';
  }

  /** Save & Continue: commit the current step's draft, then advance. */
  next() {
    const i = this.state.currentStep;
    if (this.#personalizationIncomplete(i)) {
      this.#setError('Please enter your embroidery text, or turn off personalization.');
      return;
    }
    if (i < this.config.steps.length) this.#commit(i);
    this.#setError('');
    this.#setStep(Math.min(i + 1, this.config.steps.length));
  }

  /** Last step: commit it, then add the bundle — but only once EVERY step has a
   *  selection. If any step is unsaved, jump to it instead of adding. */
  finish() {
    if (this.#personalizationIncomplete(this.state.currentStep)) {
      this.#setError('Please enter your embroidery text, or turn off personalization.');
      return;
    }
    if (this.state.currentStep < this.config.steps.length) this.#commit(this.state.currentStep);
    // A step left with personalization on and no text would otherwise be charged
    // for an Embroidered Pocket carrying an empty line_1.
    const blankText = this.config.steps.findIndex((_, i) => this.#personalizationIncomplete(i));
    if (blankText !== -1) {
      this.#setError('Please enter your embroidery text, or turn off personalization.');
      this.#setStep(blankText);
      return;
    }
    // Require a committed PRODUCT in every step (a saved state with no product —
    // e.g. an un-chosen accessory — doesn't count).
    const firstIncomplete = this.config.steps.findIndex((_, i) => !this.getStepSelection(i)?.product);
    if (firstIncomplete !== -1) {
      this.dispatchEvent(new CustomEvent('bundle:incomplete', { bubbles: true, detail: { step: firstIncomplete } }));
      this.#setStep(firstIncomplete);
      return;
    }
    this.complete();
  }
  prev() {
    this.#setStep(Math.max(this.state.currentStep - 1, 0));
  }

  /**
   * Open the "See Details" product drawer for a step. The drawer
   * (`<bundle-details-drawer>`) listens for the bubbling `bundle:details`
   * event and renders the resolved product.
   * @param {{step:number}} data
   */
  openDetails(data) {
    const step = typeof data === 'object' ? data.step : Number(data);
    const sel = this.getDraftSelection(step);
    // The drawer prices off `product.price` / `product.compareAt`, so hand it
    // the CHOSEN variant's numbers — otherwise a size change wouldn't show there.
    const product = sel?.product ? { ...sel.product, ...this.#variantPricing(sel.variant) } : null;
    this.dispatchEvent(new CustomEvent('bundle:details', { bubbles: true, detail: { step, product } }));
  }

  /** Variant price/compareAt as a patch to overlay on a product payload. */
  #variantPricing(variant) {
    if (!variant) return {};
    return { price: variant.price, compareAt: variant.compareAt ?? variant.price };
  }

  /** Open the drawer for the ACCIDENT PROTECTION product (not the bag). */
  openAccidentDetails(data) {
    const step = typeof data === 'object' ? data.step : Number(data);
    const ap = this.config.steps[step]?.accidentProtection;
    if (!ap) return;
    const variants = ap.variants || [];
    const chosen =
      variants.find((v) => String(v.id) === String(this.state.steps[step]?.accidentVariantId)) ||
      [...variants].sort((a, b) => a.price - b.price)[0];
    const product = {
      productId: ap.productId,
      handle: ap.handle,
      title: ap.title,
      image: ap.image,
      images: ap.image ? [ap.image] : [],
      price: chosen?.price ?? 0,
      compareAt: chosen?.price ?? 0,
      available: true,
      detail: ap.detail,
      features: ap.features,
      specs: ap.specs,
      whyBuy: ap.whyBuy,
    };
    this.dispatchEvent(new CustomEvent('bundle:details', { bubbles: true, detail: { step, product } }));
  }

  /** Open the drawer for the active group's ADD-ON product (e.g. "Big Frosty"). */
  openAddOnDetails(data) {
    const step = typeof data === 'object' ? data.step : Number(data);
    const addOn = this.getDraftSelection(step)?.group?.addOn;
    if (!addOn) return;
    const product = {
      productId: addOn.productId,
      handle: addOn.handle,
      title: addOn.title,
      image: addOn.image,
      images: Array.isArray(addOn.images) && addOn.images.length ? addOn.images : addOn.image ? [addOn.image] : [],
      price: addOn.price ?? 0,
      compareAt: addOn.price ?? 0,
      available: true,
      detail: addOn.detail,
      features: addOn.features,
      specs: addOn.specs,
      whyBuy: addOn.whyBuy,
    };
    this.dispatchEvent(new CustomEvent('bundle:details', { bubbles: true, detail: { step, product } }));
  }

  /** @param {number} to */
  #setStep(to) {
    const from = this.state.currentStep;
    if (to === from || Number.isNaN(to)) return;
    this.state.currentStep = to;
    this.dispatchEvent(new CustomEvent('bundle:step', { bubbles: true, detail: { from, to } }));
    this.#emitUpdate();
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  // ── Completion → cart line items ─────────────────────────────────────────────

  /**
   * Build the /cart/add payload. Children share a generated `_bundle_id` so the
   * Cart Transform Function (Phase 1, Dev) can merge them into one parent line.
   */
  buildCartItems() {
    const bundleId = `${this.config.handle}-${Date.now()}`;
    /** @type {Array<{id:string, quantity:number, properties:Record<string,string>}>} */
    const items = [];

    // No separate parent line is added: the Cart Transform Function's
    // `linesMerge` presents the merged children AS the bundle product variant
    // (`_bundle_parent`), so the cart shows one "The Loma Bundle" line at the
    // bundle price. Adding a $0 parent line would orphan/self-merge.
    const bundleDiscount = String(this.config.discount ?? 0);

    for (let i = 0; i < this.config.steps.length; i++) {
      const sel = this.getStepSelection(i);
      // The line id is the CHOSEN variant (the shopper's size), never the
      // product's default one. Null when the step has no committed product —
      // and Shopify's /cart/add.js 422s on an empty id, so skip.
      const variantId = sel?.variant?.id;
      if (!variantId) continue;
      // _bundle_parent = the bundle product's merge-target variant; the Cart
      // Transform Function merges all lines sharing _bundle_id into it.
      const base = {
        // NAMESPACED `_sgbundle_*` (not `_bundle_*`): the legacy v1 bundle
        // (assets/bundle-builder.js), bundle-cart-handler.js, and the older
        // "Sunday Golf Bundle" cart transform all key off `_bundle_id` and would
        // claim our lines — discarding our merge. Unique keys keep v2 isolated.
        _sgbundle_id: bundleId,
        _sgbundle_step: sel.step.label,
        _sgbundle_parent: String(this.config.parentVariantId ?? ''),
        _sgbundle_product: String(this.config.productId ?? ''),
        // Fixed-$ bundle saving (dollars); the Cart Transform prices the merged
        // line at sum(components) − this.
        _sgbundle_discount: bundleDiscount,
      };
      const productProps = { ...base };

      // Personalization → embroidery. Mirror the normal bag PDP's line-item
      // properties (Fulfil channel script keys) on BOTH the bag line and the
      // Embroidered Pocket line: line_1 / line_2 / Thread-Color / SKU + the
      // upsell price.
      //
      // Deliberately NO `_parent_id` on the pocket, unlike the normal PDP:
      // `_parent_id` triggers the store's EXISTING embroidery cart transform,
      // which merges the pocket into the bag and strips our `_sgbundle_*` in
      // the process. The bundle transform then can't claim the bag, so the
      // bundle never forms and the shopper gets separate full-price lines.
      // (Same root cause as 5807a11, which hit the fixed-bundle path.)
      // Fulfil still gets the embroidery text off both lines.
      let personalizationLine = null;
      // Resolved from the SELECTED bag's own embroidery config (step-level is
      // only the fallback), so the fee variant charged and the character limits
      // always belong to the bag actually going in the cart.
      const pers = this.getPersonalization(i, sel.product);
      if (sel.state.personalizationOn && pers) {
        const feeId = pers.feeVariantId;
        // The whole block hangs off `feeId`, matching v1 (bundle-builder.js).
        // The embroidery props ARE the Fulfil channel keys, so attaching them
        // to the bag line without also adding the chargeable Embroidered
        // Pocket line gets the embroidery produced and shipped for free — a
        // personalization metaobject with no fee_variant used to do exactly
        // that. No fee variant, no embroidery.
        if (feeId) {
          const emb = this.#embroideryProps(pers, sel.state.personalizationProps, sel.product);
          // Last line of defence: no text, no charge. The UI blocks this in
          // next()/finish(), but a restored draft or a programmatic value could
          // still arrive here, and billing for a blank stitch is the worst
          // failure mode available.
          if (emb.line_1) {
            Object.assign(productProps, emb); // bag line carries the embroidery props
            personalizationLine = {
              id: feeId,
              quantity: 1,
              properties: { ...base, ...emb, 'Upsell Price': emb.upsell_price },
            };
          }
        }
      }

      items.push({ id: variantId, quantity: 1, properties: productProps });
      if (personalizationLine) items.push(personalizationLine);
      if (sel.state.addOnSelected && sel.group.addOn?.variantId) {
        items.push({ id: sel.group.addOn.variantId, quantity: 1, properties: { ...base } });
      }
      if (sel.state.accidentVariantId) {
        items.push({ id: sel.state.accidentVariantId, quantity: 1, properties: { ...base } });
      }
    }
    return { bundleId, items };
  }

  /**
   * Translate the bundle's personalization inputs into the same line-item
   * properties the normal bag PDP sends (so the Fulfil channel script and cart
   * display stay consistent). Text fields map in order to line_1 / line_2.
   *
   * Thread-Color follows the PDP popup's precedence exactly
   * (`applyThreadColor()`): the BAG's own `custom.embroidery_colors`
   * ("Label|code" → code) first, then the configured colour list. Order matters
   * — `threadColors` may come from a STEP-level personalization, whose list is
   * shared by every bag in the step, so reading it first would stitch one
   * generic colour (and its label, not the Fulfil code) on every bag. For a
   * product-level offer the two agree anyway: `threadColors[0]` is built FROM
   * the bag's own thread. A thread/colour personalization field, if one is ever
   * configured, wins over both.
   * @param {{feePrice?:number, maxChars?:number, fields?:string[], threadColors?:{label:string,value:string}[]}} pers
   * @param {Record<string,string>} props
   * @param {{embroideryColor?:string}} [product]
   * @returns {Record<string,string>}
   */
  #embroideryProps(pers, props, product) {
    const fields = Array.isArray(pers.fields) ? pers.fields : Object.keys(props || {});
    const isColor = (f) => /colou?r|thread/i.test(f);
    const textFields = fields.filter((f) => !isColor(f));
    const colorField = fields.find(isColor);

    let threadColor = colorField ? props[colorField] ?? '' : '';
    if (!threadColor) {
      const raw = product?.embroideryColor;
      if (typeof raw === 'string' && raw.length) {
        const parts = raw.split('|');
        threadColor = parts.length > 1 ? parts[1] : parts[0];
      }
    }
    if (!threadColor) threadColor = pers.threadColors?.[0]?.value ?? '';

    // Clamp to the SELECTED bag's limit: text typed on a bag that allowed more
    // characters survives in state when the shopper switches bags, and the
    // input's `maxlength` doesn't apply to a value set programmatically.
    const max = Number(pers.maxChars) > 0 ? Number(pers.maxChars) : Infinity;
    // Sanitize here too, not just on input: state can be restored from a draft
    // saved before these rules existed, or set programmatically. Trimmed so a
    // whitespace-only line reads as empty to the caller's `if (emb.line_1)`.
    const line = (field) =>
      BundleBuilderComponent.sanitizeEmbroideryText(props[field] ?? '')
        .slice(0, max)
        .trim();

    return {
      line_1: line(textFields[0]),
      line_2: line(textFields[1]),
      'Thread-Color': threadColor,
      upsell_price: ((Number(pers.feePrice) || 0) / 100).toFixed(2),
      SKU: 'Embroidery Pocket',
    };
  }

  /** @type {boolean} */ #submitting = false;

  /**
   * Add every bundle line to the cart in one request, then honor `atcMode`:
   *   "checkout" → redirect to /checkout
   *   "cart"     → refresh cart sections in place (stay on page)
   *   "drawer"   → refresh + auto-open the cart drawer
   * The `bundle:complete` event still fires (analytics / Cart Transform hooks).
   */
  async complete() {
    if (this.#submitting) return;
    const { items } = this.buildCartItems();
    if (!items.length) return;

    this.#submitting = true;
    this.toggleAttribute('data-submitting', true);
    this.dispatchEvent(new CustomEvent('bundle:complete', { bubbles: true, detail: { items, totals: this.totals } }));

    const mode = this.config.atcMode || 'checkout';
    this.#setError('');
    // Optimistically open the cart drawer the instant the shopper commits, so it
    // appears immediately (with a loading state) instead of after the round-trip.
    if (mode === 'drawer') {
      const drawer = document.querySelector('cart-drawer-component');
      // @ts-ignore — open() is defined on CartDrawerComponent.
      if (drawer && typeof drawer.open === 'function') drawer.open();
    }

    const addUrl =
      (typeof Theme !== 'undefined' && Theme?.routes?.cart_add_url) || '/cart/add.js';
    const sections = this.#cartSectionIds();

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(sections ? { items, sections } : { items }),
      });
      const result = await response.json();

      if (!response.ok || result?.status) {
        // Shopify names the MERGED line in its message ("'The Loma Bundle' is
        // already sold out") even when it's one component that hit its stock
        // limit, so keep the text generic enough to be true.
        this.#setError(result?.description || result?.message || 'Add to cart failed');
        this.dispatchEvent(
          new CustomEvent('bundle:error', { bubbles: true, detail: { message: result?.message || 'Add to cart failed' } })
        );
        return;
      }

      if (mode === 'checkout') {
        window.location.assign('/checkout');
        return;
      }

      // "cart": go to the cart page. The bundle template's layout has no
      // header cart, so "stay on the page" showed the shopper nothing after a
      // successful add — the setting looked broken.
      // "drawer": layout/bundle.liquid renders the theme's cart drawer (hidden
      // trigger) for this mode; it was opened optimistically above, so just
      // refresh its contents with the freshly-rendered sections. If the drawer
      // somehow isn't on the page (cart_type set to "page"), fall back to /cart.
      const drawer = document.querySelector('cart-drawer-component');
      if (mode === 'cart' || !drawer) {
        window.location.assign('/cart');
        return;
      }
      this.dispatchEvent(new CartAddEvent({}, this.id || 'bundle-builder', { source: 'bundle-builder', sections: result.sections }));
      // The refresh above morphs the whole "cart-drawer" section, and the
      // re-rendered <dialog> comes back closed — so (re)open it AFTER the
      // morph, not just optimistically before the request.
      requestAnimationFrame(() => {
        // @ts-ignore — open() is defined on CartDrawerComponent.
        if (typeof drawer.open === 'function') drawer.open();
      });
    } catch (error) {
      console.error('[bundle-builder] cart/add failed', error);
      this.#setError('Network error adding bundle to cart. Please try again.');
      this.dispatchEvent(
        new CustomEvent('bundle:error', { bubbles: true, detail: { message: 'Network error adding bundle to cart' } })
      );
    } finally {
      this.#submitting = false;
      this.removeAttribute('data-submitting');
    }
  }

  /**
   * Show (or clear, with '') the add-to-cart error in every `ref="error"`
   * slot the step and bottom-bar snippets render. Errors used to go only to a
   * `bundle:error` event nothing rendered, so a failed add looked like a dead
   * button.
   * @param {string} message
   */
  #setError(message) {
    this.toggleAttribute('data-error', Boolean(message));
    for (const el of this.querySelectorAll('[ref="error"]')) {
      el.textContent = message;
      el.toggleAttribute('hidden', !message);
    }
  }

  /** Comma-joined section ids the cart drawer/page need re-rendered, or null. */
  #cartSectionIds() {
    const ids = new Set();
    for (const el of document.querySelectorAll('cart-drawer-component[data-section-id], cart-items-component[data-section-id]')) {
      const id = /** @type {HTMLElement} */ (el).dataset.sectionId;
      if (id) ids.add(id);
    }
    return ids.size ? Array.from(ids).join(',') : null;
  }

  // ── Broadcast ───────────────────────────────────────────────────────────────

  /** Format cents using the shop money format. */
  money(cents) {
    return formatMoney(cents, this.config.moneyFormat);
  }

  #emitUpdate() {
    // Sanitize BEFORE reading totals — a toggle left on for a bag that can't be
    // embroidered would otherwise price a fee that will never reach the cart.
    this.#dropUnavailablePersonalization();
    this.dispatchEvent(
      new CustomEvent('bundle:update', {
        bubbles: true,
        detail: { currentStep: this.state.currentStep, steps: this.state.steps, totals: this.totals },
      })
    );
  }
}

if (!customElements.get('bundle-builder')) {
  customElements.define('bundle-builder', BundleBuilderComponent);
}
